import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo, useRef } from 'react'
import { useToast } from './ToastContext'
import { useAuth } from './AuthContext'
import {
  getDiscogsProfile,
  startDiscogsConnect,
  disconnectDiscogs,
  runDiscogsSync,
  type SyncProgress,
} from '../api/discogs'
import { useDiscogsCache } from '../hooks/useDiscogsLists'

export type { SyncProgress }

interface DiscogsUser {
  username: string
}

/**
 * Connection / auth state + sync orchestration.
 *
 * This value changes rarely (login, logout, OAuth callback) so consumers of
 * `useDiscogsConnection` are insulated from the high-frequency sync-progress
 * ticks, which live in a separate context (`useSyncStatus`). The actual
 * collection/wantlist DATA is server-state and lives in React Query — see
 * `useCollection` / `useWantlist` in hooks/useDiscogsLists.
 */
interface DiscogsConnectionValue {
  user: DiscogsUser | null
  isAuthenticated: boolean
  isLoggingIn: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  /** Imperative full sync of the wantlist (page-by-page Discogs → local DB). */
  fetchWantlist: () => Promise<void>
  /** Imperative full sync of the collection (page-by-page Discogs → local DB). */
  fetchCollection: () => Promise<void>
}

interface SyncStatusValue {
  syncProgress: SyncProgress
}

const DiscogsConnectionContext = createContext<DiscogsConnectionValue | undefined>(undefined)
const SyncStatusContext = createContext<SyncStatusValue | undefined>(undefined)

interface DiscogsProviderProps {
  children: ReactNode
}

const IDLE_SYNC: SyncProgress = { type: null, current: 0, total: 0, totalItems: 0 }

export function DiscogsProvider({ children }: DiscogsProviderProps) {
  const { showToast } = useToast()
  const { setListData, clearAll } = useDiscogsCache()
  const { user: authenticatedUser, isLoading: isAuthLoading } = useAuth()

  const [user, setUser] = useState<DiscogsUser | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [syncProgress, setSyncProgress] = useState<SyncProgress>(IDLE_SYNC)

  const isSyncingWantlistRef = useRef(false)
  const isSyncingCollectionRef = useRef(false)
  const autoSyncOnConnectRef = useRef(false)

  // Keep the Discogs connection in step with application login/logout. This
  // must react to an in-app login too, not just to a full page refresh.
  useEffect(() => {
    if (isAuthLoading) return
    if (!authenticatedUser) {
      setUser(null)
      clearAll()
      return
    }

    let cancelled = false
    getDiscogsProfile()
      .then((profile) => {
        if (!cancelled) setUser(profile)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
    return () => { cancelled = true }
  }, [authenticatedUser, isAuthLoading, clearAll])

  // Remove legacy localStorage keys
  useEffect(() => {
    localStorage.removeItem('discogs_user')
    localStorage.removeItem('discogs_wantlist_data')
    localStorage.removeItem('discogs_collection_data')
  }, [])

  // Listen for OAuth callback postMessage from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'discogs_auth_success' && event.data?.user) {
        setUser({ username: event.data.user.username })
        setIsLoggingIn(false)
        autoSyncOnConnectRef.current = true
      } else if (event.data?.type === 'discogs_auth_error') {
        setIsLoggingIn(false)
        showToast(event.data.message || 'Discogs connection failed', 'error')
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [showToast])

  // Fetch wantlist — syncs page-by-page from Discogs into DB, then loads IDs
  // into the React Query cache. Sync protocol (batching, cleanup, partial
  // handling) is preserved verbatim in `runDiscogsSync`.
  const fetchWantlist = useCallback(async () => {
    if (!user) return
    if (isSyncingWantlistRef.current) {
      console.log('Already syncing wantlist, skipping')
      return
    }

    isSyncingWantlistRef.current = true

    try {
      const { data, totalSynced, partial, failureMessage } = await runDiscogsSync('wantlist', setSyncProgress)
      setListData('wantlist', data)
      console.log('Wantlist sync complete:', totalSynced, 'items')
      if (partial) {
        throw new Error(failureMessage || 'Wantlist sync was partial. Some pages failed; try again later.')
      }
    } catch (err: any) {
      console.error('Wantlist fetch error:', err)
      throw err
    } finally {
      setSyncProgress(IDLE_SYNC)
      isSyncingWantlistRef.current = false
    }
  }, [user, setListData])

  // Fetch collection — syncs page-by-page from Discogs into DB, then loads IDs
  const fetchCollection = useCallback(async () => {
    if (!user) return
    if (isSyncingCollectionRef.current) {
      console.log('Already syncing collection, skipping')
      return
    }

    isSyncingCollectionRef.current = true

    try {
      const { data, totalSynced, partial, failureMessage } = await runDiscogsSync('collection', setSyncProgress)
      setListData('collection', data)
      console.log('Collection sync complete:', totalSynced, 'items')
      if (partial) {
        throw new Error(failureMessage || 'Collection sync was partial. Some pages failed; try again later.')
      }
    } catch (err: any) {
      console.error('Collection fetch error:', err)
      throw err
    } finally {
      setSyncProgress(IDLE_SYNC)
      isSyncingCollectionRef.current = false
    }
  }, [user, setListData])

  // Auto-sync collection then wantlist on first connect
  useEffect(() => {
    if (!user || !autoSyncOnConnectRef.current) return
    autoSyncOnConnectRef.current = false
    const run = async () => {
      try {
        await fetchCollection()
        await fetchWantlist()
      } catch (err: any) {
        console.error('Discogs auto-sync failed:', err)
        showToast(err?.response?.data?.message || err?.message || 'Discogs sync failed', 'error')
      }
    }
    run()
  }, [user, fetchCollection, fetchWantlist, showToast])

  // Initiate OAuth flow — redirects the current tab to Discogs then back
  const login = useCallback(async () => {
    setIsLoggingIn(true)
    try {
      const data = await startDiscogsConnect()
      if (data.success && data.authorizeUrl) {
        sessionStorage.setItem('discogs_auth_return', window.location.pathname)
        window.location.href = data.authorizeUrl
        return
      }
      if (data.configured === false) {
        setIsLoggingIn(false)
        showToast('Discogs OAuth not configured. An admin must add the consumer credentials in the Admin panel.', 'error')
        return
      }
      setIsLoggingIn(false)
      showToast(data.message || 'Failed to start Discogs login. Please try again.', 'error')
    } catch (error) {
      console.error('[Discogs] Login error:', error)
      setIsLoggingIn(false)
      showToast('Could not reach the server. Please check your connection.', 'error')
    }
  }, [showToast])

  // Disconnect — clears the server-side Discogs record and the cached lists
  const logout = useCallback(async () => {
    try {
      await disconnectDiscogs()
    } catch {
      // best effort
    }
    setUser(null)
    clearAll()
  }, [clearAll])

  // Connection value — stable across sync-progress ticks. Only changes when
  // auth/login state genuinely changes, so unrelated consumers don't re-render.
  const connectionValue = useMemo<DiscogsConnectionValue>(() => ({
    user,
    isAuthenticated: !!user,
    isLoggingIn,
    login,
    logout,
    fetchWantlist,
    fetchCollection,
  }), [user, isLoggingIn, login, logout, fetchWantlist, fetchCollection])

  const syncValue = useMemo<SyncStatusValue>(() => ({ syncProgress }), [syncProgress])

  return (
    <DiscogsConnectionContext.Provider value={connectionValue}>
      <SyncStatusContext.Provider value={syncValue}>
        {children}
      </SyncStatusContext.Provider>
    </DiscogsConnectionContext.Provider>
  )
}

/** Discogs connection/auth state + sync triggers. */
export function useDiscogsConnection(): DiscogsConnectionValue {
  const context = useContext(DiscogsConnectionContext)
  if (!context) {
    throw new Error('useDiscogsConnection must be used within DiscogsProvider')
  }
  return context
}

/** The currently-connected Discogs user (or null). */
export function useDiscogsUser(): DiscogsUser | null {
  return useDiscogsConnection().user
}

/**
 * Live sync-progress. Isolated in its own context so its high-frequency ticks
 * only re-render the components that actually show sync status.
 */
export function useSyncStatus(): SyncStatusValue {
  const context = useContext(SyncStatusContext)
  if (!context) {
    throw new Error('useSyncStatus must be used within DiscogsProvider')
  }
  return context
}
